// Capabilities per connector. Each one becomes a node:
//   kind "action" -> palette node (connector_action: HTTP via the connector)
//   kind "event"  -> trigger ("Ao ...", app dispara webhook -> inicia o fluxo)
//
// Os 10 principais têm method/path/body REAIS (conforme a doc pública de cada
// API) — mas ainda precisam de teste com credencial real e, em alguns, de IDs
// da conta (pipeline, etapa, tags). O `path`/`body` são editáveis no nó.
const A = (key, label, method = "POST", path = "", body = "") => ({
  key,
  label,
  kind: "action",
  method,
  path,
  body,
});
// out (opcional): schema de SAÍDA do evento — os campos que chegam no
// steps.trigger.event.body quando o gatilho dispara. Serve para o nó/copiloto
// conhecerem o formato do JSON SEM precisar explicar (mapeamento de variáveis).
//   { fields: [{ path, desc }], sample: {...} }
// setup (opcional): passo a passo de como ligar o evento DO LADO DA FERRAMENTA,
// quando ela não dispara sozinha (ex.: exige montar uma campanha/automação que
// chame a nossa URL). Vira um "?" no nó do canvas que abre o guia.
//   { title, intro?, steps: [string], note? }
const E = (key, label, out = null, setup = null) => ({
  key,
  label,
  kind: "event",
  ...(out ? { out } : {}),
  ...(setup ? { setup } : {}),
});

// Schema do ticket do Movidesk (webhook de gatilho + GET /tickets compartilham o
// mesmo objeto). Paths relativos a steps.trigger.event.body.
const MOVIDESK_TICKET = {
  fields: [
    { path: "id", desc: "id interno do ticket" },
    { path: "protocol", desc: "protocolo (número visível do chamado)" },
    { path: "subject", desc: "assunto do ticket" },
    { path: "baseStatus", desc: "status base: New | InAttendance | Stopped | Canceled | Closed" },
    { path: "status", desc: "status atual (texto configurado na conta, ex. 'Fechado')" },
    { path: "category", desc: "categoria do ticket" },
    { path: "urgency", desc: "urgência" },
    { path: "lastUpdate", desc: "data/hora da última atualização (ISO 8601)" },
    { path: "clients[0].id", desc: "id do cliente do ticket" },
    { path: "clients[0].businessName", desc: "nome do cliente / empresa" },
    { path: "clients[0].email", desc: "e-mail do cliente" },
    { path: "clients[0].phone", desc: "telefone do cliente (use como destino da chamada NPS)" },
    { path: "owner.businessName", desc: "agente responsável pelo atendimento" },
  ],
  sample: {
    id: 12345,
    protocol: "2026000123",
    subject: "Problema no login",
    baseStatus: "Closed",
    status: "Fechado",
    category: "Suporte",
    urgency: "Normal",
    lastUpdate: "2026-06-23T14:05:00.000Z",
    clients: [{ id: "987", businessName: "ACME Ltda", email: "contato@acme.com", phone: "5531999990000" }],
    owner: { businessName: "Ana Suporte" },
  },
};

// Schema da mensagem de WhatsApp RECEBIDA (gatilho "Ao receber mensagem").
// Paths relativos a steps.trigger.event.body. ATENÇÃO: para RESPONDER, o destino
// é o `from` (quem te mandou) — NÃO invente `sender`/`telefone`.
const WHATSAPP_INBOUND = {
  fields: [
    { path: "from", desc: "número de quem ENVIOU (DDI+DDD+número). USE COMO DESTINO ao responder." },
    { path: "text", desc: "texto da mensagem recebida (quando type=text)" },
    { path: "name", desc: "nome do contato (perfil do WhatsApp)" },
    { path: "type", desc: "tipo da mensagem: text | image | audio | document | ..." },
    { path: "messageId", desc: "id único da mensagem recebida (wamid)" },
    { path: "contact.wa_id", desc: "identificador WhatsApp do contato" },
    { path: "metadata.phone_number_id", desc: "id do número da conta que recebeu" },
    { path: "timestamp", desc: "horário do envio (epoch, segundos)" },
  ],
  sample: {
    from: "5519999990000",
    name: "Maria Silva",
    text: "oi",
    type: "text",
    messageId: "wamid.HBgNNTUxOTk5OTk5MDAwMBUC",
    timestamp: "1782216904",
    contact: { profile: { name: "Maria Silva" }, wa_id: "5519999990000" },
    metadata: { display_phone_number: "551930950000", phone_number_id: "1203274539533684" },
  },
};

export const CONNECTOR_CAPS = {
  // ── WhatsApp ──────────────────────────────────────────────────────────
  gupshup: [
    A(
      "enviar_whatsapp",
      "Enviar WhatsApp",
      "POST",
      "/msg",
      '{\n  "destination": "{{steps.trigger.event.body.telefone}}",\n  "message": "{\\"type\\":\\"text\\",\\"text\\":\\"{{transcript}}\\"}"\n}'
    ),
    A(
      "enviar_template",
      "Enviar template",
      "POST",
      "/template/msg",
      '{\n  "destination": "{{steps.trigger.event.body.telefone}}",\n  "template": "{\\"id\\":\\"SEU_TEMPLATE_ID\\",\\"params\\":[]}"\n}'
    ),
    E("ao_receber_mensagem", "Ao receber mensagem", WHATSAPP_INBOUND),
  ],
  // WhatsApp NÃO oficial via QR Code (self-hosted / Baileys). O envio compila
  // para o verbo `whatsapp_qr_send` (override em verbs.js) — não é HTTP como os
  // demais connector_action. O inbound chega pelo socket local (nativo).
  whatsapp_qr: [
    A("enviar_mensagem", "Enviar mensagem"),
    A("consultar_perfil", "Extrair nome e foto"),
    E("ao_receber_mensagem", "Ao receber mensagem", WHATSAPP_INBOUND),
  ],
  // ── Mensageria ────────────────────────────────────────────────────────
  // Telegram Bot API. O token vai no CAMINHO (api.telegram.org/bot<TOKEN>),
  // montado pelo executor — por isso path aqui começa em /sendMessage.
  // `chat_id` aceita o id numérico do chat/grupo ou @canal.
  telegram: [
    A(
      "enviar_mensagem",
      "Enviar mensagem",
      "POST",
      "/sendMessage",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "text": "{{transcript}}"\n}'
    ),
    A(
      "enviar_foto",
      "Enviar foto",
      "POST",
      "/sendPhoto",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "photo": "https://exemplo.com/imagem.jpg",\n  "caption": ""\n}'
    ),
    A(
      "enviar_documento",
      "Enviar documento",
      "POST",
      "/sendDocument",
      '{\n  "chat_id": "{{steps.trigger.event.body.chat_id}}",\n  "document": "{{steps.trigger.call.recordingPublicUrl}}",\n  "caption": "Gravação da chamada"\n}'
    ),
  ],
  // ── CRM ───────────────────────────────────────────────────────────────
  rdstation: [
    A("criar_negocio", "Criar negócio", "POST", "/deals", '{\n  "deal": { "name": "{{steps.trigger.event.body.nome}}" }\n}'),
    A("buscar_contato", "Buscar contato por telefone", "GET", "/contacts?phone={{steps.trigger.event.body.telefone}}"),
    A("registrar_tarefa", "Registrar tarefa/atividade", "POST", "/tasks", '{\n  "task": { "subject": "Ligação", "notes": "{{transcript}}" }\n}'),
    E("ao_criar_lead", "Ao criar lead"),
    E("ao_mudar_etapa", "Ao mudar etapa do funil"),
  ],
  pipedrive: [
    A("criar_negocio", "Criar negócio", "POST", "/deals", '{\n  "title": "{{steps.trigger.event.body.titulo}}"\n}'),
    A("mover_etapa", "Mover etapa do negócio", "PUT", "/deals/{{steps.trigger.event.body.deal_id}}", '{\n  "stage_id": 1\n}'),
    A("registrar_atividade", "Registrar atividade da ligação", "POST", "/activities", '{\n  "subject": "Ligação",\n  "type": "call",\n  "note": "{{transcript}}",\n  "done": 1\n}'),
    A("buscar_contato", "Buscar contato por telefone", "GET", "/persons/search?term={{steps.trigger.event.body.telefone}}&fields=phone"),
    E("ao_criar_negocio", "Ao criar negócio"),
    E("ao_mudar_etapa", "Ao mudar etapa"),
  ],
  bitrix: [
    A("criar_lead", "Criar lead", "POST", "/crm.lead.add.json", '{\n  "fields": {\n    "TITLE": "{{steps.trigger.event.body.titulo}}",\n    "PHONE": [{ "VALUE": "{{steps.trigger.event.body.telefone}}", "VALUE_TYPE": "WORK" }]\n  }\n}'),
    A("registrar_atividade", "Registrar atividade", "POST", "/crm.activity.add.json", '{\n  "fields": { "SUBJECT": "Ligação", "DESCRIPTION": "{{transcript}}" }\n}'),
    A("buscar_contato", "Buscar contato por telefone", "POST", "/crm.contact.list.json", '{\n  "filter": { "PHONE": "{{steps.trigger.event.body.telefone}}" }\n}'),
    E("ao_criar_lead", "Ao criar/atualizar lead"),
  ],
  hubspot: [
    A("upsert_contato", "Criar/atualizar contato", "POST", "/crm/v3/objects/contacts", '{\n  "properties": {\n    "phone": "{{steps.trigger.event.body.telefone}}",\n    "firstname": "{{steps.trigger.event.body.nome}}"\n  }\n}'),
    A("criar_negocio", "Criar negócio", "POST", "/crm/v3/objects/deals", '{\n  "properties": { "dealname": "{{steps.trigger.event.body.titulo}}" }\n}'),
    A("registrar_chamada", "Registrar chamada", "POST", "/crm/v3/objects/calls", '{\n  "properties": {\n    "hs_call_body": "{{transcript}}",\n    "hs_call_direction": "OUTBOUND"\n  }\n}'),
    E("ao_criar_contato", "Ao criar contato"),
  ],

  // ── ERP ───────────────────────────────────────────────────────────────
  bling: [
    A("consultar_pedido", "Consultar pedido", "GET", "/pedidos/vendas/{{steps.trigger.event.body.pedido_id}}"),
    A("consultar_produto", "Consultar produto/preço", "GET", "/produtos/{{steps.trigger.event.body.produto_id}}"),
    A("buscar_contato", "Buscar contato", "GET", "/contatos?pesquisa={{steps.trigger.event.body.telefone}}"),
    E("ao_receber_pedido", "Ao receber pedido"),
    E("ao_mudar_status_pedido", "Ao mudar status do pedido"),
  ],

  // ── Cobrança ──────────────────────────────────────────────────────────
  asaas: [
    A("consultar_cobranca", "Consultar cobrança do cliente", "GET", "/payments?customer={{steps.trigger.event.body.cliente_id}}"),
    A("gerar_2via", "Gerar 2ª via (linha digitável)", "GET", "/payments/{{steps.trigger.event.body.cobranca_id}}/identificationField"),
    A("consultar_pagamento", "Consultar status de pagamento", "GET", "/payments/{{steps.trigger.event.body.cobranca_id}}"),
    E("ao_vencer_cobranca", "Ao vencer cobrança"),
    E("ao_confirmar_pagamento", "Ao confirmar pagamento"),
  ],

  // ── Helpdesk ──────────────────────────────────────────────────────────
  // Movidesk usa formulários amigáveis (`form`): o usuário só vê os campos que
  // importam (ex.: "ID do ticket"); método/caminho/corpo são montados por baixo.
  movidesk: [
    {
      key: "abrir_ticket", label: "Abrir ticket", kind: "action", method: "POST", path: "/tickets",
      form: [
        { name: "assunto", label: "Assunto", type: "text", default: "{{steps.trigger.event.body.assunto}}", helperText: "Assunto do chamado. Aceita variável." },
        { name: "clienteId", label: "ID do cliente", type: "text", default: "{{steps.trigger.event.body.cliente_id}}", helperText: "Cliente do ticket (id no Movidesk)." },
        { name: "descricao", label: "Descrição", type: "textarea", default: "{{transcript}}", helperText: "Primeira ação do ticket (texto)." },
      ],
      body: d => {
        // nó legado (sem os campos do form) guardava o body como string JSON
        if (!d.assunto && !d.clienteId && !d.descricao && d.body) return d.body;
        return {
          type: 2,
          subject: d.assunto || "",
          category: "Atendimento",
          clients: d.clienteId ? [{ id: String(d.clienteId) }] : [],
          actions: [{ type: 2, description: d.descricao || "{{transcript}}" }],
        };
      },
    },
    {
      // 1 ticket específico: ?id=<numero> (não precisa $select). Campo do webhook: `id`.
      key: "buscar_ticket", label: "Buscar ticket por id", kind: "action", method: "GET",
      form: [
        { name: "ticketId", label: "ID do ticket", type: "text", default: "{{steps.trigger.event.body.id}}", placeholder: "{{steps.trigger.event.body.id}} ou 700001", helperText: "Número do ticket a buscar. Aceita variável." },
      ],
      path: d => (d.ticketId != null ? `/tickets?id=${String(d.ticketId).trim()}` : (d.path || "/tickets?id={{steps.trigger.event.body.id}}")),
    },
    {
      // Listar/filtrar exige $select (senão 400); $filter/$top recomendados.
      key: "listar_tickets", label: "Listar tickets (filtro)", kind: "action", method: "GET",
      form: [
        { name: "baseStatus", label: "Status", type: "select", default: "New", options: [
          { value: "New", label: "Novos" }, { value: "InAttendance", label: "Em atendimento" },
          { value: "Stopped", label: "Parados" }, { value: "Closed", label: "Fechados" }, { value: "Canceled", label: "Cancelados" },
        ], helperText: "Filtra pelos tickets neste status." },
        { name: "top", label: "Máximo de resultados", type: "number", default: 20, helperText: "Quantos tickets trazer (até 100)." },
      ],
      path: d => `/tickets?$select=id,protocol,subject,status,baseStatus,owner,clients&$filter=baseStatus eq '${d.baseStatus || "New"}'&$top=${d.top || 20}`,
    },
    {
      key: "atualizar_ticket", label: "Atualizar ticket", kind: "action", method: "PATCH",
      form: [
        { name: "ticketId", label: "ID do ticket", type: "text", default: "{{steps.trigger.event.body.id}}", helperText: "Ticket a atualizar. Aceita variável." },
        { name: "status", label: "Novo status", type: "text", default: "Em atendimento", placeholder: "Ex.: Em atendimento, Fechado", helperText: "Status exatamente como configurado na conta Movidesk." },
      ],
      path: d => (d.ticketId != null ? `/tickets?id=${String(d.ticketId).trim()}` : (d.path || "/tickets?id={{steps.trigger.event.body.id}}")),
      body: d => ({ status: d.status || "Em atendimento" }),
    },
    E("ao_abrir_ticket", "Ao abrir ticket", MOVIDESK_TICKET),
    E("ao_mudar_status_ticket", "Ao mudar status do ticket", MOVIDESK_TICKET),
  ],

  // ── Marketing ─────────────────────────────────────────────────────────
  activecampaign: [
    A("upsert_contato", "Criar/atualizar contato", "POST", "/api/3/contact/sync", '{\n  "contact": {\n    "email": "{{steps.trigger.event.body.email}}",\n    "phone": "{{steps.trigger.event.body.telefone}}"\n  }\n}'),
    A("aplicar_tag", "Aplicar tag", "POST", "/api/3/contactTags", '{\n  "contactTag": { "contact": "{{steps.trigger.event.body.contato_id}}", "tag": "1" }\n}'),
    A("add_automacao", "Adicionar a automação", "POST", "/api/3/contactAutomations", '{\n  "contactAutomation": { "contact": "{{steps.trigger.event.body.contato_id}}", "automation": "1" }\n}'),
    E("ao_disparar_automacao", "Ao disparar automação"),
  ],
  brevo: [
    A("enviar_email", "Enviar e-mail", "POST", "/smtp/email", '{\n  "to": [{ "email": "{{steps.trigger.event.body.email}}" }],\n  "sender": { "name": "Numera", "email": "no-reply@seu-dominio.com" },\n  "subject": "{{steps.trigger.event.body.assunto}}",\n  "htmlContent": "<p>{{transcript}}</p>"\n}'),
    A("enviar_sms", "Enviar SMS", "POST", "/transactionalSMS/sms", '{\n  "sender": "Numera",\n  "recipient": "{{steps.trigger.event.body.telefone}}",\n  "content": "{{transcript}}"\n}'),
    A("add_lista", "Adicionar contato a lista", "POST", "/contacts", '{\n  "email": "{{steps.trigger.event.body.email}}",\n  "listIds": [1]\n}'),
  ],

  // ── Tier-2 (apenas rótulo/endpoint placeholder por enquanto) ──────────
  ploomes: [
    A("criar_negocio", "Criar negócio", "POST", "/Deals"),
    A("buscar_cliente", "Buscar cliente por telefone", "GET", "/Contacts"),
    E("ao_criar_negocio", "Ao criar negócio"),
  ],
  agendor: [
    A("criar_negocio", "Criar negócio"),
    A("registrar_atividade", "Registrar atividade"),
    E("ao_mudar_etapa", "Ao mudar etapa"),
  ],
  tiny: [
    A("consultar_pedido", "Consultar pedido", "POST", "/pedido.obter.php"),
    A("consultar_produto", "Consultar produto/estoque", "POST", "/produto.obter.estoque.php"),
    E("ao_mudar_status_pedido", "Ao mudar status do pedido"),
  ],
  omie: [
    A("consultar_cliente", "Consultar cliente"),
    A("consultar_financeiro", "Consultar financeiro / 2ª via"),
    E("ao_baixar_conta", "Ao baixar/gerar conta"),
  ],
  mautic: [
    A("add_campanha", "Adicionar a campanha"),
    A("aplicar_tag", "Aplicar tag/segmento"),
    // O Mautic NÃO tem evento de webhook para campanha nem para segmento (a lista
    // nativa cobre contato, formulário, e-mail, página e pontuação). O que existe
    // é a AÇÃO de campanha "Send a webhook". Por isso este gatilho é montado do
    // lado do Mautic, e o guia abaixo é obrigatório — sem ele o cliente ativa o
    // gatilho, não configura nada e fica esperando um evento que nunca vem.
    // O payload deste gatilho é DEFINIDO POR QUEM CONFIGURA a ação no Mautic
    // (a ação "Send a webhook" tem editor de campos com tokens). Então nós
    // PRESCREVEMOS o formato: o guia manda montar exatamente estes campos e o
    // `out` declara os mesmos — assim o seletor de variáveis mostra "Telefone",
    // "E-mail", e ninguém precisa abrir JSON.
    E(
      "ao_entrar_campanha",
      "Ao entrar em campanha ou segmento",
      {
        fields: [
          { path: "telefone", desc: "telefone do contato (DDD+número). USE COMO DESTINO ao ligar/enviar SMS." },
          { path: "nome", desc: "nome do contato" },
          { path: "email", desc: "e-mail do contato" },
          { path: "contato_id", desc: "id do contato no Mautic (para consultar/atualizar depois)" },
          { path: "campanha", desc: "nome da campanha que disparou" },
        ],
        sample: {
          telefone: "51981184103",
          nome: "Maria Silva",
          email: "maria@exemplo.com.br",
          contato_id: "841600",
          campanha: "Clientes novos - boas-vindas",
        },
      },
      {
        title: "Como ligar este gatilho no Mautic",
        intro:
          "O Mautic não avisa sozinho quando um contato entra numa campanha ou segmento — quem avisa é uma ação dentro da campanha. São 4 passos, uma única vez por campanha.",
        steps: [
          "No Mautic, abra Campanhas e crie (ou edite) a campanha que deve disparar este fluxo.",
          "Em Fontes da campanha, escolha o segmento que você quer observar — todo contato que entrar nele entra na campanha.",
          "No fluxo da campanha, adicione uma AÇÃO do tipo “Enviar um webhook” (Send a webhook). Use método POST e cole a URL abaixo.",
          "Ainda na ação, monte os dados exatamente com estes 5 campos (copie e cole os tokens):\n" +
            "telefone = {contactfield=phone}\n" +
            "nome = {contactfield=firstname}\n" +
            "email = {contactfield=email}\n" +
            "contato_id = {contactfield=id}\n" +
            "campanha = nome da campanha (texto livre)\n" +
            "Salve e publique a campanha.",
        ],
        note:
          "Os nomes dos campos importam: é por eles que o fluxo enxerga “Telefone”, “Nome” e “E-mail” no seletor de variáveis, sem você mexer em JSON. Se o contato usar celular em outro campo, troque por {contactfield=mobile}. Preencheu o Token de verificação aqui? Some o mesmo valor no cabeçalho x-webhook-secret da ação, senão a chamada é recusada.",
      }
    ),
  ],
  zendesk: [
    A("abrir_ticket", "Abrir ticket", "POST", "/tickets.json"),
    E("ao_abrir_ticket", "Ao abrir ticket"),
  ],
  iugu: [
    A("consultar_cobranca", "Consultar cobrança", "GET", "/invoices/{{id}}"),
    E("ao_confirmar_pagamento", "Ao confirmar pagamento"),
  ],
  mercadopago: [
    A("consultar_pagamento", "Consultar status de pagamento", "GET", "/v1/payments/{{id}}"),
    E("ao_aprovar_pagamento", "Ao aprovar pagamento"),
  ],
  zenvia: [
    A("enviar_sms", "Enviar SMS", "POST", "/channels/sms/messages"),
    A("enviar_whatsapp", "Enviar WhatsApp", "POST", "/channels/whatsapp/messages"),
    E("ao_receber_mensagem", "Ao receber mensagem"),
  ],
  calendly: [
    A("buscar_horarios", "Buscar horários", "GET", "/event_type_available_times"),
    E("ao_agendar", "Ao agendar reunião"),
    E("ao_cancelar", "Ao cancelar"),
  ],
  airtable: [
    A("criar_registro", "Criar registro", "POST", "/{{baseId}}/{{table}}"),
    A("buscar_registro", "Buscar registro", "GET", "/{{baseId}}/{{table}}"),
  ],
  webhook: [A("enviar_dados", "Enviar dados (HTTP)", "POST", "")],
};
